<script setup lang="ts">
import type { VListCardRowProps, VListCardRowSlotProps } from "./VListCard.types";
import { computed, inject, useId } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import VHelpTooltip from "../HelpTooltip/VHelpTooltip.vue";
import VIcon from "../Icon/VIcon.vue";
import { ListCardPassthrough, ListCardRoleCells } from "./listCardRoles";
import { listCardRowCva, listCardRowSubtitleCva, listCardRowTitleCva } from "./VListCard.cva";
import { LIST_CARD_KEY } from "./VListCard.types";

defineOptions({
  name: "VListCardRow",
});

/**
 * One row of a `VListCard`: leading content (a checkbox, an icon), the title with an optional subtitle, and
 * trailing content (a chip, a switch, buttons). The row can carry a control whose label is the whole row, act as
 * one big button or link, or open extra content under itself.
 */
const {
  title = undefined,
  subtitle = undefined,
  help = undefined,
  as = undefined,
  controlId = undefined,
  clickable = false,
  href = undefined,
  to = undefined,
  disabled = false,
  selected = false,
  tone = "default",
  dense = undefined,
  wrap = false,
  stack = false,
  align = "center",
  subheader = false,
  expandable = false,
  expandLabel = undefined,
  testId = undefined,
} = defineProps<VListCardRowProps>();

const emit = defineEmits<{
  click: [event: MouseEvent]
}>();

const slots = defineSlots<{
  /** Content before the title, such as the row's checkbox. `subtitleId` describes the control. */
  leading?: (props: VListCardRowSlotProps) => unknown
  /** Replaces the title and subtitle block. */
  default?: (props: VListCardRowSlotProps) => unknown
  /** Rich title instead of `title` (inside the label, button or link of an interactive row). */
  title?: () => unknown
  /** Rich subtitle instead of `subtitle`. */
  subtitle?: () => unknown
  /**
   * Content after the title, such as a chip, a switch or row buttons. Stays clickable above a stretched row. The
   * ids name or describe a control here by the row's texts (`aria-labelledby`, `aria-describedby`).
   */
  trailing?: (props: VListCardRowSlotProps) => unknown
  /** Content under the row, shown while `expanded`. Gets the same ids as `trailing`. */
  expanded?: (props: VListCardRowSlotProps) => unknown
}>();

const expanded = defineModel<boolean>("expanded", { default: false });

const { t } = useI18n();
const card = inject(LIST_CARD_KEY, null);
const titleId = useId();
const subtitleDomId = useId();
const panelId = useId();

const cardTag = computed(() => card?.tag.value ?? "div");
const isDl = computed(() => cardTag.value === "dl");
const isTable = computed(() => cardTag.value === "table");
const rowTag = computed(() => {
  if (isDl.value || isTable.value) {
    return "div";
  }
  return as ?? (cardTag.value === "ul" || cardTag.value === "ol" ? "li" : "div");
});
const columnsClass = computed(() => card?.columnsClass.value);
/** One compact line with the subtitle inline; the row's own `dense` wins over the card's. */
const isDense = computed(() => (dense ?? card?.dense.value ?? false) && !subheader);
/** A description list lays its rows out as a term and a value, not on the column grid. */
const isGrid = computed(() => Boolean(columnsClass.value) && !subheader && !isDl.value);

/*
 * Slots are not reactive: a slot that appears later (a `v-if` on the slot, a subtitle once data has loaded) is only
 * seen when it is checked during render. These checks are functions on purpose, so the template calls them on every
 * render instead of caching the first answer.
 */
function hasSubtitle(): boolean {
  return Boolean(subtitle || slots.subtitle);
}
function subtitleId(): string | undefined {
  return hasSubtitle() ? subtitleDomId : undefined;
}
function slotProps(): VListCardRowSlotProps {
  return { titleId, subtitleId: subtitleId(), controlId };
}
function hasPanel(): boolean {
  return Boolean(slots.expanded) && expanded.value;
}
function hasTrailing(): boolean {
  return Boolean(slots.trailing) || expandable;
}

/** The element that carries the title: a label for the row's control, a button, a link, or plain text. */
const titleKind = computed<"label" | "button" | "link" | "router-link" | "text">(() => {
  if (subheader) {
    return "text";
  }
  if (controlId) {
    return "label";
  }
  if (to !== undefined) {
    return disabled ? "text" : "router-link";
  }
  if (href !== undefined) {
    return disabled ? "text" : "link";
  }
  if (clickable) {
    return "button";
  }
  return "text";
});
const stretched = computed(() => titleKind.value !== "text");
/** Every stretched row, a row with a label for its control included, shows the hover state of a clickable row. */
const interactive = computed(() => stretched.value);

const titleTag = computed(() => {
  switch (titleKind.value) {
    case "label":
      return "label";
    case "button":
      return "button";
    case "link":
      return "a";
    case "router-link":
      return RouterLink;
    default:
      return "span";
  }
});

function titleAttrs() {
  switch (titleKind.value) {
    case "label":
      return { for: controlId };
    case "button":
      return { "type": "button", disabled, "aria-describedby": subtitleId() };
    case "link":
      return { "href": href, "aria-describedby": subtitleId() };
    case "router-link":
      return { "to": to, "aria-describedby": subtitleId() };
    default:
      return {};
  }
}

const rowClass = computed(() => listCardRowCva({
  subheader,
  dense: isDense.value,
  selected,
  interactive: interactive.value,
  disabled,
}));
const alignClass = computed(() => (align === "start" ? "items-start" : "items-center"));
/** Trailing content drops under the title block on small screens; never in a grid, where every element is a cell. */
const stacks = computed(() => stack !== false && !isGrid.value);

/**
 * The line with the leading content, the title block and the trailing content. While the expanded part is open the
 * line is the positioning context of the stretched label, button or link, so that part and the gap above it are
 * not covered by its hit area.
 */
function lineClass() {
  const position = hasPanel() ? "relative" : null;
  if (isGrid.value) {
    return ["grid gap-x-4", alignClass.value, columnsClass.value, position];
  }
  if (stacks.value) {
    return ["flex flex-wrap gap-x-3 gap-y-2 sm:flex-nowrap", alignClass.value, position];
  }
  return ["flex gap-3", alignClass.value, position];
}
/** A description list row is the line itself: a `div` that holds the term and the value. */
function dlRowClass() {
  return [rowClass.value, "flex flex-wrap gap-x-3", alignClass.value];
}
function termClass() {
  return ["flex min-w-0 flex-1 gap-3", alignClass.value, hasPanel() ? "relative" : null];
}
/**
 * In a grid every leading and trailing element is a cell of its own. Side content sits above the stretched
 * label, button or link of the title, so its own controls stay clickable.
 */
const sideClass = computed(() => (isGrid.value
  ? "contents *:relative *:z-[1]"
  : ["relative z-[1] flex shrink-0 gap-2.5", alignClass.value]));
/**
 * With align start the leading content (a dot, an icon, a checkbox) is centred on the first title line, not stuck to
 * the top of it: the box is one text-sm line high (20px) and grows with taller content such as an avatar.
 */
const leadingClass = computed(() => (isGrid.value || align !== "start"
  ? sideClass.value
  : "relative z-[1] flex min-h-5 shrink-0 items-center gap-2.5"));
const trailingClass = computed(() => {
  if (!stacks.value) {
    return sideClass.value;
  }
  return [sideClass.value, "max-sm:basis-full", stack === "full" ? "max-sm:*:min-w-0 max-sm:*:flex-1" : null];
});
function titleClass() {
  if (subheader) {
    return wrap ? "block min-w-0 whitespace-normal wrap-break-word" : "block min-w-0 truncate";
  }
  return listCardRowTitleCva({
    tone: disabled ? "muted" : tone,
    disabled,
    stretched: stretched.value,
    hitArea: hasPanel() ? "line" : "row",
    wrap,
  });
}
const subtitleClass = computed(() => listCardRowSubtitleCva({ wrap, dense: isDense.value }));
/** Dense: title and subtitle share one line, aligned on the text baseline. */
const blockClass = computed(() => (isDense.value
  ? "flex min-w-0 flex-1 flex-row items-baseline gap-2"
  : "flex min-w-0 flex-1 flex-col gap-0.5"));
const toggleLabel = computed(() => expandLabel ?? (expanded.value ? t("base.collapse_details") : t("base.expand_details")));

/** In table mode the row's own element is only a frame; the line and the open expanded part are the table rows. */
const cellRole = computed(() => (isTable.value ? "cell" : undefined));
const wrapperRole = computed(() => (isTable.value ? "none" : undefined));

function onTitleClick(event: MouseEvent) {
  if (disabled || titleKind.value !== "button") {
    return;
  }
  emit("click", event);
}
</script>

<template>
  <component
    :is="rowTag"
    :class="isDl ? dlRowClass() : rowClass"
    :role="wrapperRole"
    :data-disabled="disabled || undefined"
    :data-selected="selected || undefined"
    :data-testid="testId"
  >
    <component
      :is="isDl ? ListCardPassthrough : 'div'"
      v-bind="isDl ? {} : { class: lineClass(), role: isTable ? 'row' : undefined }"
    >
      <component :is="isDl ? 'dt' : ListCardPassthrough" v-bind="isDl ? { class: termClass() } : {}">
        <div v-if="$slots.leading" :class="leadingClass" :role="wrapperRole">
          <ListCardRoleCells :role="cellRole">
            <slot name="leading" v-bind="slotProps()" />
          </ListCardRoleCells>
        </div>

        <div :class="blockClass" :role="cellRole">
          <slot v-bind="slotProps()">
            <!-- The help sits after the title, not inside it: the title can be the row's label, button or link. -->
            <component :is="help ? 'div' : ListCardPassthrough" v-bind="help ? { class: 'flex min-w-0 items-center gap-1.5' } : {}">
              <component
                :is="titleTag"
                v-if="title || $slots.title"
                :id="titleId"
                v-bind="titleAttrs()"
                :class="titleClass()"
                :title="wrap ? undefined : title"
                @click="onTitleClick"
              >
                <slot name="title">
                  {{ title }}
                </slot>
              </component>
              <VHelpTooltip v-if="help" class="relative z-[1]" :content="help" :subject="title" />
            </component>
            <span
              v-if="hasSubtitle()"
              :id="subtitleDomId"
              :class="subtitleClass"
              :title="wrap ? undefined : subtitle"
            >
              <slot name="subtitle">
                {{ subtitle }}
              </slot>
            </span>
          </slot>
        </div>
      </component>

      <component
        :is="isDl ? 'dd' : 'div'"
        v-if="hasTrailing() || isDl"
        :class="trailingClass"
        :role="wrapperRole"
      >
        <ListCardRoleCells :role="cellRole">
          <slot name="trailing" v-bind="slotProps()" />
        </ListCardRoleCells>
        <component :is="isTable ? 'span' : ListCardPassthrough" v-if="expandable" v-bind="isTable ? { role: 'cell', class: 'flex items-center' } : {}">
          <button
            type="button"
            class="relative z-[1] flex size-8 shrink-0 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-dark-600"
            :aria-expanded="expanded"
            :aria-controls="hasPanel() ? panelId : undefined"
            :aria-label="toggleLabel"
            :disabled="disabled"
            data-testid="list-card-row-toggle"
            @click="expanded = !expanded"
          >
            <VIcon
              name="chevron-down"
              class="text-xs transition-transform"
              :class="{ 'rotate-180': expanded }"
              aria-hidden="true"
            />
          </button>
        </component>
      </component>
    </component>

    <component
      :is="isDl ? 'dd' : 'div'"
      v-if="hasPanel()"
      :id="panelId"
      class="relative z-[1] mt-3"
      :class="{ 'basis-full': isDl }"
      :role="isTable ? 'row' : undefined"
      data-testid="list-card-row-expanded"
    >
      <component :is="isTable ? 'div' : ListCardPassthrough" v-bind="isTable ? { role: 'cell' } : {}">
        <slot name="expanded" v-bind="slotProps()" />
      </component>
    </component>
  </component>
</template>
